import React from 'react';
import parse from "date-fns/parse";
import DepartureStationPopover
    from "@Pages/stationToStation/features/departureStationPopover/components/DepartureStationPopover";
import BusChangePopover from "@Pages/stationToStation/features/busChangePopover/components/BusChangePopover";
import PricePopover from "@Pages/stationToStation/features/pricePopover/components/PricePopover";
import StationToStationDatePicker from "./StationToStationDatePicker";
import formatDate from "@DateTime/functions/formatDate";
import Translation from "@Translation/components/Translation";
import useFerrySupportedLocale from "@GetByFerry/hooks/useFerrySupportedLocale";
import BestPriceRouteType from "@Pages/stationToStation/types/BestPriceRoute.type";
import IconMTicket from "@Icons/ticket/IconMTicket";
import {Colors} from "@Styles/Colors.style";
import IconRating from "@Icons/IconRating";

interface BestPriceOnDateRoutePanelProps {
    route: BestPriceRouteType
    index: number,
}

function BestPriceOnDateRoutePanel({route, index}: BestPriceOnDateRoutePanelProps) {
    const parseAndStripSeconds = (date: string) => (
        formatDate(parse(date, 'HH:mm:ss', new Date()), 'HH:mm')
    )

    const departureTime = parseAndStripSeconds(route.departureTime)
    const arrivalTime = parseAndStripSeconds(route.arrivalTime)
    const {locale, transportType} = useFerrySupportedLocale()

    return (
        <div className="panel panel-default">
            <div
                className={`panel-heading${route.isApi ? ' panel-heading--api ' : ''}`}
                role="tab"
                id={`heading${route.id}`}
            >
                <div className="gb--route">
                    <div className="gb--route-element gb--route-station gb--departure gb--limit-portal-width">
                        <DepartureStationPopover
                            departureTime={departureTime}
                            departureStation={route.departureStationName}
                            routeId={route.id}
                        />
                    </div>

                    <div className="gb--route-element gb--connect">
                        <span className="gb--route-time">{route.travelTime}</span>
                        <div className="gb--connect-divider">
                            <span className="gb--connect-circle gb--connect-circle--green"/>
                            <span className="gb--connect-line"/>
                            <span className="gb--connect-circle gb--connect-circle--orange"/>
                        </div>
                        {!!route.busChange.length && (
                            <BusChangePopover
                                routeId={route.id}
                                stops={route.busChange}
                                index={index}
                            />
                        )}
                        {!route.isApi && !route.busChange.length && (
                            <span className="gb--connect__bus-change">
                                DIRECT
                            </span>
                        )}
                    </div>

                    <div className="gb--route-element gb--route-station">
                        <span className={`gb--emphasize gb--emphasize-default gb--route-time`}>
                            {arrivalTime}
                        </span>
                        <span className="gb--truncate">{route.destinationStationName}</span>
                    </div>
                    <div className="gb--route-element gb--route-ratings ">
                        <div className="gb--column gb--column company-rating">
                            {route.companyName && (
                                <span className="gb--truncate gb--company-code gb--emphasize-default">
                                    {route.companyName}
                                </span>
                            )}
                            {route.companyRating > 0 && (
                                <div className="gb--row gb--row-justify gb--row-middle">
                                    <div
                                        className="gb--row gb--row-middle company-rating__score"
                                    >
                                        <IconRating fill={Colors.warningColor} stroke={Colors.warningColor}/>
                                        {route.companyRating}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="gb--route-price gb--route-status">
                        <PricePopover
                            index={index}
                            price={route.priceOnDate}
                            preparedPrices={route.preparedPrices}
                            passengerGroups={route.passengerGroups}
                            routeId={route.id}
                        />
                    </div>
                    <div className="gb--route-element">
                        <div className="gb--route-element-badges">
                            {route.hasETicket && (
                                <span className="gb--badge gb--badge-left gb--badge-green">
                                    <IconMTicket fill={Colors.neutralBlackColor}/>
                                    M-Ticket
                                </span>
                            )}
                            {route.bestRated && (
                                <span className="gb--badge gb--badge-left gb--badge-green">
                                    <IconMTicket fill={Colors.neutralBlackColor}/>
                                    <Translation translationKey="search_results.sort_by.rating"/>
                                </span>
                            )}
                            {route.cheapest && (
                                <span className="gb--badge gb--badge-left gb--badge-green">
                                    <IconMTicket fill={Colors.neutralBlackColor}/>
                                    <Translation translationKey="search_results.sort_by.price"/>
                                </span>
                            )}
                            {route.fastest && (
                                <span className="gb--badge gb--badge-left gb--badge-blue">
                                    <IconMTicket fill={Colors.neutralBlackColor}/>
                                    <Translation translationKey="search_results.sort_by.travel_time"/>
                                </span>
                            )}
                            {route.bestOfferOn && (
                                <span className="gb--badge gb--badge-left gb--badge-blue">
                                    <Translation translationKey="search_results.best_offer_on"/>
                                    {' '}
                                    {route.bestOfferOn}
                                </span>
                            )}
                        </div>

                        {route.showDatePicker && (
                            <div className="gb--route-datepicker">
                                <StationToStationDatePicker
                                    locale={locale}
                                    routeId={route.id}
                                    transportType={transportType}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BestPriceOnDateRoutePanel;